// Mouse helpers for rows that scroll sideways (Block Company Timeline's years at any width; Block
// Company Listing's Location row and the Press Release list's Year row on mobile
// widths). Touch and
// horizontal trackpad gestures (deltaX) already scroll these natively; a plain mouse can't, so:
//
// - Wheel: a vertical wheel over the row scrolls the row instead - but only while it can still
//   move that way, so at either end the wheel goes back to scrolling the page rather than
//   trapping it.
// - Drag: press and drag the row sideways (grab-to-scroll), so the thin scrollbar isn't the only
//   way. Only starts after DRAG_THRESHOLD px of movement, so a plain click on a pill/year still
//   clicks; a click that ends a real drag is swallowed instead of selecting what's under it.
//
// Rows that don't overflow (e.g. every pill row on desktop) are left alone, and so are rows the
// CSS has locked (overflow-x hidden/clip - Block Company Timeline's years on mobile widths, which
// only its own script scrolls). The row's look while scrollable/dragging comes from
// molecules/scroll-row.css (.scroll-row.is-scrollable/.is-dragging).

const DRAG_THRESHOLD = 5;

// How long after the last wheel event a new one starts from where the row actually is, rather
// than from the target still being glided to.
const WHEEL_IDLE_MS = 200;

// Read per check, not once: a media query can lock or unlock the row at any time.
function isLocked(row: HTMLElement): boolean {
    const overflowX = getComputedStyle(row).overflowX;
    return overflowX === "hidden" || overflowX === "clip";
}

export function bindHorizontalWheel(row: HTMLElement): void {
    // Wheel steps add up into one target the row glides towards. A separate smooth scrollBy per
    // step restarted the animation on every event (and from wherever the previous one had got
    // to), which made a fast wheel stutter.
    let target = 0;
    let lastWheel = 0;

    row.addEventListener("wheel", (event) => {
        // Ctrl+wheel and trackpad pinch (sent as wheel with ctrlKey) zoom the page - leave them be.
        if (event.ctrlKey || event.deltaY === 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            return;
        }
        const maxScroll = row.scrollWidth - row.clientWidth;
        if (maxScroll <= 0 || isLocked(row)) {
            return;
        }
        const canMove = event.deltaY > 0 ? row.scrollLeft < maxScroll - 1 : row.scrollLeft > 0;
        if (!canMove) {
            return;
        }
        event.preventDefault();

        if (event.timeStamp - lastWheel > WHEEL_IDLE_MS) {
            target = row.scrollLeft;
        }
        lastWheel = event.timeStamp;
        // deltaMode 1 = lines (e.g. Firefox with a notched wheel): roughly 40px per line.
        const delta = event.deltaMode === 1 ? event.deltaY * 40 : event.deltaY;
        target = Math.min(maxScroll, Math.max(0, target + delta));
        row.scrollTo({ left: target, behavior: "smooth" });
    }, { passive: false });
}

// Returns the .is-scrollable re-check, for a caller that changes the row's content without
// resizing the row itself (e.g. hiding some of its items), which the ResizeObserver can't see.
export function bindDragScroll(row: HTMLElement): () => void {
    let startX = 0;
    let startScroll = 0;
    let pointerId: number | null = null;
    let dragging = false;

    const isScrollable = (): boolean => row.scrollWidth - row.clientWidth > 1 && !isLocked(row);

    // .is-scrollable shows the grab cursor only while there's actually something to drag.
    const updateScrollable = (): void => {
        row.classList.toggle("is-scrollable", isScrollable());
    };
    updateScrollable();
    if ("ResizeObserver" in window) {
        new ResizeObserver(updateScrollable).observe(row);
    }

    row.addEventListener("pointerdown", (event) => {
        if (event.pointerType !== "mouse" || event.button !== 0 || !isScrollable()) {
            return;
        }
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = row.scrollLeft;
        dragging = false;
    });

    row.addEventListener("pointermove", (event) => {
        if (event.pointerId !== pointerId) {
            return;
        }
        const dx = event.clientX - startX;
        if (!dragging) {
            if (Math.abs(dx) < DRAG_THRESHOLD) {
                return;
            }
            dragging = true;
            row.setPointerCapture(event.pointerId);
            row.classList.add("is-dragging");
        }
        row.scrollLeft = startScroll - dx;
    });

    const end = (event: PointerEvent): void => {
        if (event.pointerId !== pointerId) {
            return;
        }
        pointerId = null;
        if (!dragging) {
            return;
        }
        if (row.hasPointerCapture(event.pointerId)) {
            row.releasePointerCapture(event.pointerId);
        }
        // The click that follows the mouseup of a drag would otherwise select the pill/year it
        // happens to end on - swallow just that one. It's dispatched right after pointerup, before
        // the timeout below runs; when it doesn't come at all (drag ended outside the row), the
        // timeout removes the listener so it can't swallow a later, real click.
        const swallowClick = (click: MouseEvent): void => {
            click.preventDefault();
            click.stopPropagation();
        };
        row.addEventListener("click", swallowClick, { capture: true, once: true });
        // .is-dragging stays on until that click has been dispatched: Engage's document-level
        // click tracking runs before swallowClick and skips clicks inside .is-dragging.
        setTimeout(() => {
            row.removeEventListener("click", swallowClick, { capture: true });
            row.classList.remove("is-dragging");
            dragging = false;
        }, 0);
    };
    row.addEventListener("pointerup", end);
    row.addEventListener("pointercancel", end);

    // Links/images inside would otherwise start the browser's own drag-and-drop.
    row.addEventListener("dragstart", (event) => event.preventDefault());

    return updateScrollable;
}

// Wheel + drag, for every sideways-scrolling row. Returns bindDragScroll's re-check.
export function bindScrollRow(row: HTMLElement): () => void {
    bindHorizontalWheel(row);
    return bindDragScroll(row);
}
